import { API_URL } from "../../../../utils/constant";

const BillFormatA4Page = ({ companyData, data, withHeader = true }) => {
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatCurrency = (amount) => {
    return (parseFloat(amount) || 0).toFixed(2);
  };

  const calculateTotals = () => {
    let originalInclusiveSubtotal = 0;

    const lineCalculations = (data.product_data || []).map(item => {
      const isFree = item.is_free === 1 || item.is_free === true || item.is_free === '1' || item.is_free === 'true' || (parseFloat(item.qty) > 0 && parseFloat(item.free_qty) >= parseFloat(item.qty));
      const totalItemQty = item.is_loose ? 1 : (parseFloat(item.qty) || 0);
      const freeQty = isFree ? totalItemQty : Math.min(totalItemQty, parseFloat(item.free_qty || 0));
      const billedQty = isFree ? 0 : Math.max(0, totalItemQty - freeQty);

      const gstPercent = parseFloat(item.gst) || 0;
      const price = parseFloat(item.price) || 0;
      const inclusivePrice = item.gst_included ? price : price * (1 + gstPercent / 100);
      const billedGross = inclusivePrice * billedQty;

      let itemDisc = 0;
      if (!isFree && item.discount_amount !== undefined && item.discount_amount !== null && parseFloat(item.discount_amount) > 0) {
        itemDisc = parseFloat(item.discount_amount);
      } else if (!isFree && parseFloat(item.discount_value || 0) > 0) {
        if (item.discount_type === 'fixed') {
          itemDisc = Math.min(parseFloat(item.discount_value), billedGross);
        } else {
          itemDisc = (billedGross * parseFloat(item.discount_value)) / 100;
        }
      }

      const lineNet = isFree ? 0 : Math.max(0, billedGross - itemDisc);
      originalInclusiveSubtotal += lineNet;

      return {
        ...item,
        isFree,
        totalItemQty,
        freeQty,
        billedQty,
        gstPercent,
        inclusivePrice,
        billedGross,
        itemDisc,
        lineNet
      };
    });

    const discountAmount = parseFloat(data.discountAmount || data.discount_amount || 0) || 0;
    const discountedInclusiveSubtotal = Math.max(originalInclusiveSubtotal - discountAmount, 0);

    let totalTax = 0;
    let taxableAmount = 0;

    const processedItems = lineCalculations.map(item => {
      const itemProportion = originalInclusiveSubtotal > 0 ? item.lineNet / originalInclusiveSubtotal : 0;
      const itemFinalInclusive = discountedInclusiveSubtotal * itemProportion;

      const itemTaxAmount = itemFinalInclusive > 0 ? (itemFinalInclusive - (itemFinalInclusive / (1 + item.gstPercent / 100))) : 0;
      const itemTaxableAmount = itemFinalInclusive - itemTaxAmount;

      taxableAmount += itemTaxableAmount;
      totalTax += itemTaxAmount;

      return {
        ...item,
        itemDiscountedTotal: itemFinalInclusive,
        itemTaxableAmount,
        itemTaxAmount,
        itemDiscount: item.itemDisc
      };
    });

    return {
      originalSubtotal: originalInclusiveSubtotal,
      discountedSubtotal: discountedInclusiveSubtotal,
      taxableAmount,
      totalTax,
      discountAmount,
      processedItems
    };
  };

  const { originalSubtotal, discountedSubtotal, taxableAmount, totalTax, discountAmount, processedItems } = calculateTotals();

  const displayTotalTax = parseFloat((totalTax).toFixed(2));
  const displayTaxableAmount = Math.max(0, discountedSubtotal - displayTotalTax);
  const grandTotalRaw = discountedSubtotal;
  const grandTotal = Math.round(grandTotalRaw);
  const roundOff = (grandTotal - grandTotalRaw).toFixed(2);
  const totalQty = (data.product_data || []).reduce((sum, i) => sum + (parseFloat(i.qty) || 0), 0);
  const totalGrossAmount = (data.product_data || []).reduce((sum, item) => {
    const isFree = item.is_free === 1 || item.is_free === true || item.is_free === '1' || item.is_free === 'true' || (parseFloat(item.qty) > 0 && parseFloat(item.free_qty) >= parseFloat(item.qty));
    const billedQty = isFree ? 0 : Math.max(0, (parseFloat(item.qty) || 0) - (parseFloat(item.free_qty) || 0));
    return sum + ((parseFloat(item.price) || 0) * billedQty);
  }, 0);

  const numberToWords = (num) => {
    const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
    const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

    const convert = (n) => {
      if (n === 0) return 'zero';
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      if (n < 1000) return ones[Math.floor(n / 100)] + ' hundred' + (n % 100 ? ' and ' + convert(n % 100) : '');
      if (n < 100000) return convert(Math.floor(n / 1000)) + ' thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
      if (n < 10000000) return convert(Math.floor(n / 100000)) + ' lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
      return convert(Math.floor(n / 10000000)) + ' crore' + (n % 10000000 ? ' ' + convert(n % 10000000) : '');
    };

    const rupees = Math.floor(num || 0);
    if (rupees === 0) return 'ZERO ONLY';
    return (convert(rupees) + ' only').toUpperCase();
  };

  const companyName = companyData.company_name || "JNM Homoeo Sadan";

  // Pagination Chunking: Intermediate pages (with totals, but no terms/bank/sign) hold up to 20 items, Last page holds up to 14 items
  const INTERMEDIATE_ITEMS_PER_PAGE = 20;
  const LAST_PAGE_ITEMS_PER_PAGE = 14;

  const itemChunks = [];
  if (processedItems.length === 0) {
    itemChunks.push([]);
  } else if (processedItems.length <= LAST_PAGE_ITEMS_PER_PAGE) {
    itemChunks.push(processedItems);
  } else {
    let remaining = [...processedItems];
    while (remaining.length > 0) {
      if (remaining.length <= LAST_PAGE_ITEMS_PER_PAGE) {
        itemChunks.push(remaining);
        break;
      }
      // Fill intermediate page (up to 20 items), leaving at least 1 item for the last page
      let take = Math.min(remaining.length, INTERMEDIATE_ITEMS_PER_PAGE);
      if (remaining.length - take === 0 && remaining.length > LAST_PAGE_ITEMS_PER_PAGE) {
        take = remaining.length - 1;
      }
      itemChunks.push(remaining.slice(0, take));
      remaining = remaining.slice(take);
    }
  }
  const totalPages = itemChunks.length;

  const getItemHsn = (item) => {
    if (item.hsn_sac && String(item.hsn_sac).trim()) return String(item.hsn_sac).trim();
    if (item.hsn_code && String(item.hsn_code).trim()) return String(item.hsn_code).trim();
    if (item.hsn && String(item.hsn).trim()) return String(item.hsn).trim();
    if (item.hsnCode && String(item.hsnCode).trim()) return String(item.hsnCode).trim();
    if (item.hsn_no && String(item.hsn_no).trim()) return String(item.hsn_no).trim();
    if (item.taxes?.hsn_code && String(item.taxes.hsn_code).trim()) return String(item.taxes.hsn_code).trim();
    if (item.taxes?.hsn_sac && String(item.taxes.hsn_sac).trim()) return String(item.taxes.hsn_sac).trim();
    if (item.tax?.hsn_code && String(item.tax.hsn_code).trim()) return String(item.tax.hsn_code).trim();
    if (item.Product?.hsn_sac && String(item.Product.hsn_sac).trim()) return String(item.Product.hsn_sac).trim();
    if (item.Product?.hsn_code && String(item.Product.hsn_code).trim()) return String(item.Product.hsn_code).trim();
    if (item.FinalCategory?.hsn_code && String(item.FinalCategory.hsn_code).trim()) return String(item.FinalCategory.hsn_code).trim();
    if (item.RawCategory?.hsn_code && String(item.RawCategory.hsn_code).trim()) return String(item.RawCategory.hsn_code).trim();
    if (item.category?.hsn_code && String(item.category.hsn_code).trim()) return String(item.category.hsn_code).trim();
    // Default fallback for medicine products if HSN is missing in past records
    return '30049014';
  };

  const pagesHtml = itemChunks.map((chunk, pageIndex) => {
    const isLastPage = pageIndex === totalPages - 1;
    const startIndex = itemChunks.slice(0, pageIndex).reduce((sum, c) => sum + c.length, 0);
    const maxPageRows = isLastPage ? LAST_PAGE_ITEMS_PER_PAGE : INTERMEDIATE_ITEMS_PER_PAGE;
    const emptyCount = Math.max(0, maxPageRows - chunk.length);

    const chunkItemsHtml = chunk.map((item, index) => {
      const globalIndex = startIndex + index + 1;
      const cgstRate = (parseFloat(item.gst || 0) / 2).toFixed(2);
      const sgstRate = (parseFloat(item.gst || 0) / 2).toFixed(2);
      const cgstAmount = item.itemTaxAmount / 2;
      const sgstAmount = item.itemTaxAmount / 2;
      const isFree = item.is_free || false;
      const billedQty = item.billedQty !== undefined ? item.billedQty : (isFree ? 0 : Math.max(0, (parseFloat(item.qty) || 0) - (parseFloat(item.free_qty) || 0)));
      const grossTotal = (parseFloat(item.price) || 0) * billedQty;
      const itemTotal = item.itemTaxableAmount + item.itemTaxAmount;
      const batchNo = item.batch_no || '';
      const rawProductName = (item.product_name || item.name || item.Product?.name || item.product?.name || item.item_name || '').replace(/-+$/, '').trim();
      const brandName = (item.brand_name || item.brand?.name || (typeof item.brand === 'string' ? item.brand : '') || item.Brand?.name || item.Product?.Brand?.name || item.product?.Brand?.name || item.Product?.brand || item.product?.brand || '').trim();
      const productName = brandName && !rawProductName.toLowerCase().includes(brandName.toLowerCase())
        ? `${rawProductName} - ${brandName}`
        : rawProductName;

      let discPercentDisplay = '';
      if (isFree) {
        discPercentDisplay = '100%';
      } else if (item.discount_percent !== undefined && item.discount_percent !== null && item.discount_percent !== '') {
        const dp = parseFloat(item.discount_percent);
        discPercentDisplay = dp > 0 ? `${parseFloat(dp.toFixed(1))}%` : '';
      } else if (item.discount_p !== undefined && item.discount_p !== null && item.discount_p !== '') {
        discPercentDisplay = `${parseFloat(item.discount_p)}%`;
      } else if (item.discount_value > 0 && item.discount_type === 'percentage') {
        discPercentDisplay = `${parseFloat(item.discount_value)}%`;
      } else if (grossTotal > 0 && item.itemDiscount > 0) {
        discPercentDisplay = `${parseFloat(((item.itemDiscount / grossTotal) * 100).toFixed(1))}%`;
      } else if (item.discount !== undefined && item.discount !== null && parseFloat(item.discount) > 0) {
        discPercentDisplay = `${parseFloat(item.discount)}%`;
      }

      const qtyDisplay = isFree
        ? `${item.qty} (FREE)`
        : (item.free_qty > 0 ? `${item.qty} (${billedQty}+${item.free_qty}F)` : item.qty);

      return `
        <tr>
          <td class="cell c">${globalIndex}</td>
          <td class="cell l" style="padding-left:4px;">${productName}</td>
          <td class="cell c">${getItemHsn(item)}</td>
          <td class="cell c">${batchNo}</td>
          <td class="cell c">${item.expiry_date ? formatDate(item.expiry_date) : ''}</td>
          <td class="cell c" style="font-weight:bold;">${qtyDisplay}</td>
          <td class="cell c">${formatCurrency(item.price)}</td>
          <td class="cell c">${formatCurrency(grossTotal)}</td>
          <td class="cell c">${discPercentDisplay}</td>
          <td class="cell c">${formatCurrency(item.itemTaxableAmount)}</td>
          <td class="cell c">${cgstRate}</td>
          <td class="cell c">${formatCurrency(cgstAmount)}</td>
          <td class="cell c">${sgstRate}</td>
          <td class="cell c">${formatCurrency(sgstAmount)}</td>
          <td class="cell c" style="font-weight:bold;">${formatCurrency(itemTotal)}</td>
        </tr>`;
    }).join('');

    const emptyRowsHtml = Array(emptyCount).fill(`
        <tr>
          <td class="cell">&nbsp;</td><td class="cell">&nbsp;</td><td class="cell">&nbsp;</td>
          <td class="cell">&nbsp;</td><td class="cell">&nbsp;</td><td class="cell">&nbsp;</td>
          <td class="cell">&nbsp;</td><td class="cell">&nbsp;</td><td class="cell">&nbsp;</td>
          <td class="cell">&nbsp;</td><td class="cell">&nbsp;</td><td class="cell">&nbsp;</td>
          <td class="cell">&nbsp;</td><td class="cell">&nbsp;</td><td class="cell">&nbsp;</td>
        </tr>`).join('');

    return `
        <div class="page" style="${pageIndex < totalPages - 1 ? 'page-break-after: always; margin-bottom: 20px;' : ''}">

          <!-- TOP SPACER -->
          <div class="top-spacer"></div>

          <!-- META ROW -->
          <div class="meta-row">
            <div class="meta-left-col">
              <div>Invoice No: <strong>${data.bill_no}</strong></div>
              <div>Dated: <strong>${formatDate(data.createdAt)} ${formatTime(data.createdAt)}</strong></div>
              <div>Place of Supply: <strong>West Bengal (19)</strong></div>
              <div>Reverse Charge: <strong>N</strong></div>
              ${totalPages > 1 ? `<div style="font-size:7.5pt; color:#666;">Page <strong>${pageIndex + 1} of ${totalPages}</strong></div>` : ''}
            </div>
            <div class="meta-right-col">
              <div class="section-title">Details of Receiver | Billed to:</div>
              <div><strong>${data.Customer?.company_name || data.Customer?.name || 'Walk-in Customer'}</strong></div>
              ${data.Customer?.address ? `<div>Address: ${data.Customer.address}</div>` : ''}
              ${data.Customer?.gst_no ? `<div>GSTIN/Unique ID: <strong>${data.Customer.gst_no}</strong></div>` : ''}
              ${data.Customer?.dl_number ? `<div>DL No: <strong>${data.Customer.dl_number}</strong></div>` : ''}
              ${data.Customer?.phone ? `<div>Phone: ${data.Customer.phone}</div>` : ''}
            </div>
          </div>

          <!-- TABLE -->
          <table class="invoice-table">
            <thead>
              <tr>
                <th rowspan="2" style="width:3%;">S.N</th>
                <th rowspan="2" style="width:23%;">Description of Goods</th>
                <th rowspan="2" style="width:8%;">HSN/SAC</th>
                <th rowspan="2" style="width:6%;">Batch</th>
                <th rowspan="2" style="width:6%;">Exp Date</th>
                <th rowspan="2" style="width:4%;">Qty</th>
                <th rowspan="2" style="width:7%;">Rate</th>
                <th rowspan="2" style="width:7%;">Gross Amt</th>
                <th rowspan="2" style="width:5%;">Disc %</th>
                <th rowspan="2" style="width:7%;">Taxable</th>
                <th colspan="2" style="width:8.5%;">CGST</th>
                <th colspan="2" style="width:8.5%;">SGST</th>
                <th rowspan="2" style="width:8%;">Amount</th>
              </tr>
              <tr>
                <th style="width:3%;">%</th>
                <th style="width:5.5%;">Amt</th>
                <th style="width:3%;">%</th>
                <th style="width:5.5%;">Amt</th>
              </tr>
            </thead>
            <tbody>
              ${chunkItemsHtml}
              ${emptyRowsHtml}
            </tbody>
            <tfoot>
              <tr style="background: rgba(0,0,0,0.03); font-weight: bold;">
                <td colspan="5" class="cell r" style="font-weight:bold;">Total:</td>
                <td class="cell c" style="font-weight:bold;">${totalQty}</td>
                <td class="cell c">&nbsp;</td>
                <td class="cell c" style="font-weight:bold;">${formatCurrency(totalGrossAmount)}</td>
                <td class="cell c">&nbsp;</td>
                <td class="cell c" style="font-weight:bold;">${formatCurrency(displayTaxableAmount)}</td>
                <td class="cell c">&nbsp;</td>
                <td class="cell c" style="font-weight:bold;">${formatCurrency(totalTax / 2)}</td>
                <td class="cell c">&nbsp;</td>
                <td class="cell c" style="font-weight:bold;">${formatCurrency(totalTax / 2)}</td>
                <td class="cell c" style="font-weight:bold;">${formatCurrency(grandTotalRaw)}</td>
              </tr>
            </tfoot>
          </table>

          <!-- TOTALS SECTION (SHOWN ON EACH PAGE) -->
          <div class="totals-section">
            <div class="totals-left">
              <div><strong>Total Qty: ${totalQty} Pcs</strong></div>
              <div style="margin-top:4px;"><strong>Amount Chargeable (in words):</strong><br>RUPEES ${numberToWords(grandTotal)}</div>
              <div style="margin-top:6px; font-size:7pt; color:#444;">
                Payment Mode: <strong>${data.mode_of_payment ? data.mode_of_payment.toUpperCase() : 'CASH'}</strong> | Sales Exec: ${data.salesExecutive || 'Admin'}
              </div>
            </div>
            <div class="totals-right">
              <div class="totals-row">
                <span>Taxable Amount:</span>
                <span>₹${formatCurrency(displayTaxableAmount)}</span>
              </div>
              <div class="totals-row">
                <span>CGST Amount:</span>
                <span>₹${formatCurrency(totalTax / 2)}</span>
              </div>
              <div class="totals-row">
                <span>SGST Amount:</span>
                <span>₹${formatCurrency(totalTax / 2)}</span>
              </div>
              ${discountAmount > 0 ? `
              <div class="totals-row">
                <span>Discount:</span>
                <span>-₹${formatCurrency(discountAmount)}</span>
              </div>` : ''}
              ${parseFloat(roundOff) !== 0 ? `
              <div class="totals-row">
                <span>Round Off:</span>
                <span>${roundOff > 0 ? '+' : ''}${roundOff}</span>
              </div>` : ''}
              <div class="grand-row">
                <span>Grand Total:</span>
                <span>₹${formatCurrency(grandTotal)}</span>
              </div>
            </div>
          </div>

          ${isLastPage ? `
          <!-- BOTTOM SECTION (ONLY ON LAST PAGE) -->
          <div class="bottom-section">
            <div class="terms-box">
              <div style="font-weight:bold; text-decoration:underline; margin-top:6px; margin-bottom:2px;">Terms & Conditions:</div>
              1. Goods once sold will not be taken back.<br>
              2. Interest @ 18% p.a. will be charged if payment is not made within due date.<br>
              3. Subject to Kolkata Jurisdiction.
              <div style="font-weight:bold; text-decoration:underline; margin-bottom:2px;margin-top: 10px;">Bank Details:</div>
              CANARA BANK : Branch : Sarat Bose Road ,A/c No: 95031010001029. IFSC Code: CNRB0000323.<br>
              INDIAN BANK : Branch : Gariahat Road, A/C No: 20085734852 . IFSC Code : IDIB000G538.
            </div>
            <div class="sign-box">
              <div style="font-weight:bold;">For ${companyName}</div>
              <div class="sign-line">Authorised Signatory</div>
            </div>
          </div>` : `
          <!-- INTERMEDIATE PAGE FOOTER -->
          <div style="margin-top: 10px; text-align: right; font-size: 8pt; font-weight: bold; color: #6B1C23;">
            Continued on Page ${pageIndex + 2}...
          </div>`}

        </div>`;
  }).join('');

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>GST Invoice - A4 Wholesale</title>
        <style>
          @page { size: A4 portrait; margin: 0; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: Arial, 'Helvetica Neue', Helvetica, sans-serif;
            font-size: 8pt;
            color: #000;
            padding: 5px;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .page {
            width: calc(210mm - 10px);
            height: calc(297mm - 10px);
            background: ${withHeader ? "url('/bg_invoice_page2.png') no-repeat center top / 100% 100% #FDF5E6" : "transparent"};
            position: relative;
            overflow: hidden;
            padding: 0 12px;
            border: 1px solid #000;
            box-sizing: border-box;
          }

          /* TOP SPACER (matches background header height) */
          .top-spacer {
            height: 40mm;
          }

          /* META ROW */
          .meta-row {
            display: flex;
            padding: 6px 0;
            border-top: 2px solid #C8A96E;
            border-bottom: 1px solid #C8A96E;
          }
          .meta-left-col {
            width: 50%;
            font-size: 8.5pt;
            line-height: 1.6;
          }
          .meta-right-col {
            width: 50%;
            border-left: 1px solid #C8A96E;
            padding-left: 10px;
            font-size: 8pt;
            line-height: 1.5;
          }

          .section-title {
            font-size: 9pt;
            font-weight: bold;
            color: #6B1C23;
            margin-bottom: 2px;
          }

          /* TABLE */
          .invoice-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 4px;
          }
          .invoice-table th {
            font-size: 7.5pt;
            font-weight: bold;
            color: #000;
            padding: 4px 2px;
            border: 1px solid #000;
            text-align: center;
            background: rgba(255, 255, 255, 0.4);
          }
          .invoice-table td {
            font-size: 7.5pt;
            padding: 3px 2px;
            border: 1px solid #000;
            height: 18px;
            vertical-align: middle;
          }
          .c { text-align: center; }
          .l { text-align: left; }
          .r { text-align: right; }

          /* TOTALS SECTION */
          .totals-section {
            display: flex;
            border: 1px solid #000;
            border-top: none;
          }
          .totals-left {
            width: 55%;
            padding: 6px;
            border-right: 1px solid #000;
            font-size: 7.5pt;
          }
          .totals-right {
            width: 45%;
            padding: 6px;
            font-size: 8pt;
          }
          .totals-row {
            display: flex;
            justify-content: space-between;
            padding: 2px 0;
          }
          .grand-row {
            display: flex;
            justify-content: space-between;
            padding: 4px 0;
            border-top: 1.5px solid #000;
            font-size: 10pt;
            font-weight: bold;
            color: #6B1C23;
          }

          /* BOTTOM SECTION */
          .bottom-section {
            margin-top: 10px;
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
          }
          .terms-box {
            width: 55%;
            font-size: 7pt;
            line-height: 1.4;
          }
          .sign-box {
            width: 40%;
            text-align: center;
            font-size: 8pt;
          }
          .sign-line {
            margin-top: 35px;
            border-top: 1px solid #000;
            padding-top: 4px;
            font-weight: bold;
          }
        </style>
      </head>
      <body>
        ${pagesHtml}
      </body>
    </html>`;
};

export default BillFormatA4Page;